/**
 * Marketing parameter passthrough for consent-denied measurement pixels.
 *
 * While neither marketing nor analytics consent is granted, ad click ids and UTM parameters from the landing URL (KEYS)
 * are appended to same-site links and forms at click/submit time, so they reach the checkout URL, where consent-denied
 * custom pixels read them from their sandbox URL. Same idea as gtag's url_passthrough: URL-only, no cookies or storage,
 * rendered HTML is never changed. Current consumer: "BE – Google consent denied" (be-google-pixels); extend KEYS only
 * when another pixel needs its own click id.
 *
 * Decoration fails open: while the Customer Privacy API is not loaded (or fails), links are decorated. This only
 * rewrites our own internal URLs; whether anything is sent to a vendor is decided by the pixels, which fail closed.
 *
 * Does not define window.gtag or window.dataLayer (events-tracking.js relies on the app's window.gtag).
 * Compatible with internal-links-locale.js: that script repairs paths once on load, this one only appends query
 * parameters at click time. Header menu summaries are decorated via their data-link, because header.js navigates to
 * it in JS on desktop hover menus. referral-redirect.js and market-redirect-popup.js keep the query string themselves.
 * Known gap: other JS navigations (error fallbacks to /cart) are not decorated.
 *
 * Loaded async right after content_for_header in layout/theme.liquid (skipped in the theme editor).
 */
(() => {
  const KEYS = ['gclid', 'gbraid', 'wbraid', 'dclid', 'gad_source', 'gad_campaignid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'];
  const current = new URLSearchParams(location.search);
  const carried = KEYS.filter((key) => current.get(key)).map((key) => [key, current.get(key)]);
  if (!carried.length) return;

  let privacy = null;
  window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
    if (!error) privacy = window.Shopify.customerPrivacy || null;
  });

  const shouldDecorate = () => {
    if (typeof privacy?.marketingAllowed !== 'function') return true;
    return !privacy.marketingAllowed() && !privacy.analyticsProcessingAllowed();
  };

  const siteHost = (host) => host.replace(/^www\./, '');

  const isSameSite = (url) => url.protocol === location.protocol && siteHost(url.hostname) === siteHost(location.hostname);

  const decorate = (target) => {
    let url;
    try {
      url = new URL(target, location.href);
    } catch (e) {
      return null;
    }
    if (!isSameSite(url)) return null;
    const missing = carried.filter(([key]) => !url.searchParams.has(key));
    if (!missing.length) return null;
    missing.forEach(([key, value]) => url.searchParams.set(key, value));
    return url.toString();
  };

  const onLinkActivate = (event) => {
    if (!shouldDecorate()) return;
    const menuSummary = event.target.closest?.('summary[data-link]');
    if (menuSummary) {
      const decorated = decorate(menuSummary.getAttribute('data-link'));
      if (decorated) menuSummary.setAttribute('data-link', decorated);
    }
    const link = event.target.closest?.('a[href]');
    const href = link?.getAttribute('href');
    if (!href || href.startsWith('#') || /^(mailto|tel|sms|javascript):/i.test(href)) return;
    const decorated = decorate(link.href);
    if (decorated) link.href = decorated;
  };

  document.addEventListener('click', onLinkActivate, true);
  document.addEventListener('auxclick', onLinkActivate, true);

  document.addEventListener('submit', (event) => {
    if (!shouldDecorate()) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const submitter = event.submitter;
    if (submitter?.hasAttribute('formaction')) {
      const decorated = decorate(submitter.formAction);
      if (decorated) submitter.formAction = decorated;
      return;
    }
    if (form.method.toLowerCase() === 'get') {
      let action;
      try {
        action = new URL(form.action, location.href);
      } catch (e) {
        return;
      }
      if (!isSameSite(action)) return;
      carried.forEach(([key, value]) => {
        if (form.elements.namedItem(key)) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });
      return;
    }
    const decorated = decorate(form.action);
    if (decorated) form.action = decorated;
  }, true);
})();
